import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocations } from '../context/LocationContext';
import { useReviews } from '../context/ReviewContext';
import TopBar from '../components/TopBar';
import { FcGoogle } from 'react-icons/fc';
import { Star, Check, TrendingUp, MapPin, MessageSquare, Zap, Play } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isDemoMode, loginWithGoogle, enterDemoMode } = useAuth();
  const { selectedLocations, fetchLocations } = useLocations();
  const { reviews } = useReviews();

  useEffect(() => {
    if (isAuthenticated) {
      fetchLocations().catch(console.error);
    }
  }, [isAuthenticated, fetchLocations]);

  // Calculate stats
  const totalReviews = reviews?.length || 0;
  const pendingReplies = reviews?.filter(r => !r.generatedReply).length || 0;
  const aiGenerated = reviews?.filter(r => r.generatedReply).length || 0;
  const locationsCount = selectedLocations?.length || 0;

  // Stats data
  const stats = [
    {
      title: 'Total Reviews',
      value: totalReviews,
      icon: <MessageSquare className="w-6 h-6" />,
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Pending Replies',
      value: pendingReplies,
      icon: <Star className="w-6 h-6" />,
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'AI Replies Generated',
      value: aiGenerated,
      icon: <Zap className="w-6 h-6" />,
      textColor: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Locations Connected',
      value: locationsCount,
      icon: <MapPin className="w-6 h-6" />,
      textColor: 'text-green-600',
      bgColor: 'bg-green-50'
    }
  ];

  return (
    <div className="flex flex-col w-full min-h-screen bg-slate-50">
      <TopBar />

      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Dashboard Header with Generate Button */}
          {isAuthenticated && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Dashboard</h2>
                <p className="text-sm sm:text-base text-slate-600 mt-1">Overview of your review management</p>
              </div>
              <button 
                onClick={() => window.showToast?.('Generating all replies...', 'info')}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                data-testid="generate-all-replies-btn"
              >
                <Zap className="w-5 h-5" />
                <span>Generate All Replies</span>
              </button>
            </div>
          )}

          {/* Stats Cards - Only show if authenticated */}
          {isAuthenticated && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all duration-300"
                  data-testid={`stat-card-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`${stat.bgColor} p-3 rounded-xl`}>
                      <div className={stat.textColor}>
                        {stat.icon}
                      </div>
                    </div>
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="text-sm font-medium text-slate-600 mb-1">{stat.title}</h3>
                  <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Main Connection Card */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 sm:p-8 shadow-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <FcGoogle className="w-8 h-8 sm:w-10 sm:h-10" />
                </div>
                <div className="text-white">
                  <h2 className="text-lg sm:text-2xl font-bold mb-1">Google Review Responder</h2>
                  <p className="text-sm sm:text-base text-blue-100">Manage your Google Business reviews with AI</p>
                </div>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 fill-yellow-300 text-yellow-300" />
                ))}
              </div>
            </div>
          </div>

          {/* Connection Card - Centered */}
          <div className="max-w-2xl mx-auto">
            <div className={`rounded-2xl p-8 shadow-lg transition-all duration-300 ${
              isAuthenticated
                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300'
                : 'bg-white border-2 border-blue-300'
            }`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md">
                  <FcGoogle className="w-8 h-8" />
                </div>
                <div>
                  <p className={`font-bold text-xl ${
                    isAuthenticated ? 'text-green-800' : 'text-slate-700'
                  }`}>
                    {isAuthenticated ? (isDemoMode ? 'Demo Mode Active' : 'Connected') : 'Not Connected'}
                  </p>
                  <p className={`text-sm ${
                    isAuthenticated ? 'text-green-700' : 'text-slate-500'
                  }`}>
                    {isDemoMode ? 'Testing with mock reviews' : 'to Google Reviews'}
                  </p>
                </div>
                {isAuthenticated && <Check className="w-10 h-10 text-green-600 ml-auto" />}
              </div>

              {!isAuthenticated ? (
                <div className="space-y-4">
                  <button
                    onClick={loginWithGoogle}
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-4 px-6 rounded-xl hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                    data-testid="connect-google-btn"
                  >
                    Connect Google Business
                  </button>
                  
                  <div className="relative flex items-center justify-center my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-300"></div>
                    </div>
                    <div className="relative px-4 bg-white text-sm text-slate-500">or</div>
                  </div>
                  
                  <button
                    onClick={enterDemoMode}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-4 px-6 rounded-xl hover:shadow-xl transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2"
                    data-testid="demo-mode-btn"
                  >
                    <Play className="w-5 h-5" />
                    <span>Try Demo Mode</span>
                  </button>
                  <p className="text-xs text-slate-500 text-center">Test with mock reviews - no Google account needed</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <button
                    onClick={() => window.showToast?.('Posting all replies...', 'info')}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold py-4 px-6 rounded-xl hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
                    data-testid="post-all-replies-btn"
                  >
                    <span>🚀</span>
                    <span>Post All Replies</span>
                  </button>
                  <div className="space-y-3 mt-6">
                    <div className="flex items-center gap-3 text-green-800">
                      <Check className="w-5 h-5" />
                      <span className="text-sm font-medium">3 Replies ready</span>
                    </div>
                    <div className="flex items-center gap-3 text-green-800">
                      <Check className="w-5 h-5" />
                      <span className="text-sm font-medium">All replies approved</span>
                    </div>
                    <div className="flex items-center gap-3 text-green-800">
                      <Check className="w-5 h-5" />
                      <span className="text-sm font-medium">Ready to post</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions - Only if authenticated */}
          {isAuthenticated && (
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <button 
                  onClick={() => navigate('/reviews')}
                  className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                >
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">View Reviews</p>
                    <p className="text-xs text-slate-600">Manage all reviews</p>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/locations')}
                  className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
                >
                  <MapPin className="w-6 h-6 text-green-600" />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">Locations</p>
                    <p className="text-xs text-slate-600">Manage profiles</p>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/analytics')}
                  className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
                >
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">Analytics</p>
                    <p className="text-xs text-slate-600">View insights</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link>
              <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact Us</Link>
            </div>
            <p className="text-center text-xs text-slate-400 mt-4">
              © 2025 Review Responder. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
